import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function Employment() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("ko") ? "ko" : "en";

  // ---- UI State
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  const [filters, setFilters] = useState({
    sort: "LATEST", // LATEST | DEADLINE
  });

  const [page, setPage] = useState(0);
  const size = 10;

  // ---- Data State
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // 검색 디바운스
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  // 검색/정렬 변경 시 페이지 0으로
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, filters.sort]);

  const sortOptions = useMemo(
    () => [
      { value: "LATEST", label: t("employment.sort.latest") },
      { value: "DEADLINE", label: t("employment.sort.deadline") },
    ],
    [t],
  );

  const abortRef = useRef(null);

  // ---- Fetch (백엔드 연결)
  useEffect(() => {
    const fetchList = async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setErrorMsg("");

      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("size", String(size));
        if (debouncedQuery) params.set("q", debouncedQuery);
        params.set("sort", filters.sort);

        const res = await fetch(`/api/employments/list?${params.toString()}`, {
          method: "GET",
          headers: {
            "Accept-Language": locale,
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(
            `${t("employment.errors.fetch")} (HTTP ${res.status})`,
          );
        }

        const json = await res.json();
        if (json?.resultCode && json.resultCode !== "200") {
          throw new Error(json?.resultMessage || "목록 조회에 실패했습니다.");
        }
        const payload = json?.data ?? json;
        const normalized = normalizeResponse(payload);

        setItems(normalized.items);
        setTotalPages(normalized.totalPages);
        setTotalElements(normalized.totalElements);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setErrorMsg(e?.message || t("employment.errors.unknown"));
      } finally {
        setLoading(false);
      }
    };

    fetchList();
  }, [page, size, debouncedQuery, filters.sort, locale, t]);

  const onReset = () => {
    setQuery("");
    setFilters({ sort: "LATEST" });
    setPage(0);
  };

  return (
    <div className="w-full">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("employment.title")}
          </h1>
          <p className="text-sm text-gray-600">{t("employment.subtitle")}</p>
        </div>

        {/* Controls (검색 + 정렬 + 초기화) */}
        <div className="mt-6 rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
            {/* Search */}
            <div className="flex-1">
              <label className="sr-only">{t("employment.search.label")}</label>
              <div className="relative">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("employment.search.placeholder")}
                  className="w-full rounded-xl border px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                />
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  ⌕
                </div>
              </div>
            </div>

            {/* Sort */}
            <div className="w-full md:w-56">
              <Select
                label={t("employment.filters.sort")}
                value={filters.sort}
                onChange={(e) => setFilters({ sort: e.target.value })}
                options={sortOptions}
              />
            </div>

            {/* Reset */}
            <div className="flex justify-end md:justify-start">
              <button
                type="button"
                onClick={onReset}
                className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
              >
                {t("employment.actions.reset")}
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
            <div>{t("employment.result.count", { count: totalElements })}</div>
            <div>
              {t("employment.result.page", {
                current: totalPages === 0 ? 0 : page + 1,
                total: totalPages,
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {errorMsg ? (
            <ErrorState
              title={t("employment.errors.title")}
              message={errorMsg}
              onReset={onReset}
              resetLabel={t("employment.actions.reset")}
            />
          ) : loading ? (
            <SkeletonList />
          ) : items.length === 0 ? (
            <EmptyState
              title={t("employment.empty.title")}
              message={t("employment.empty.desc")}
              onReset={onReset}
              resetLabel={t("employment.actions.reset")}
            />
          ) : (
            <div className="grid gap-3">
              {items.map((it) => (
                <JobCard key={it.id} item={it} locale={locale} t={t} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-8">
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            t={t}
          />
        </div>
      </div>
    </div>
  );
}

/** ---------- Helpers ---------- */

function normalizeResponse(json) {
  if (json && Array.isArray(json.content)) {
    return {
      items: json.content,
      totalPages: Number(json.totalPages ?? 0),
      totalElements: Number(json.totalElements ?? json.content.length ?? 0),
    };
  }
  if (json && Array.isArray(json.items)) {
    return {
      items: json.items,
      totalPages: Number(json.totalPages ?? 0),
      totalElements: Number(json.totalElements ?? json.items.length ?? 0),
    };
  }
  if (Array.isArray(json)) {
    return { items: json, totalPages: 1, totalElements: json.length };
  }
  return { items: [], totalPages: 0, totalElements: 0 };
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-gray-600">{label}</span>
      <select
        value={value}
        onChange={onChange}
        className="rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
      >
        {options.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * ✅ 뱃지 제거됨 (인기/고용형태 등 표시 안함)
 */
function JobCard({ item, locale, t }) {
  const title = pickLocale(item.title, item.titleKo, item.titleEn, locale);
  const company = pickLocale(
    item.company,
    item.companyKo,
    item.companyEn,
    locale,
  );
  const locationText = pickLocale(
    item.locationText,
    item.locationKo,
    item.locationEn,
    locale,
  );

  const createdAt = item.createdAt ? formatDate(item.createdAt, locale) : "-";
  const deadline = item.deadline ? formatDate(item.deadline, locale) : "-";

  return (
    <Link
      to={`/employment/${item.id}`}
      className="group rounded-2xl border bg-white p-4 shadow-sm transition hover:shadow-md"
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold group-hover:underline">
            {title || t("employment.fallback.noTitle")}
          </h3>

          <p className="mt-1 truncate text-sm text-gray-600">
            {company || t("employment.fallback.noCompany")}
            {locationText ? ` · ${locationText}` : ""}
          </p>
        </div>

        <div className="mt-2 flex shrink-0 gap-4 text-xs text-gray-500 md:mt-0 md:flex-col md:items-end">
          <div>
            <span className="mr-1 text-gray-400">
              {t("employment.meta.created")}:
            </span>
            <span>{createdAt}</span>
          </div>
          <div>
            <span className="mr-1 text-gray-400">
              {t("employment.meta.deadline")}:
            </span>
            <span className={deadline !== "-" ? "text-gray-700" : ""}>
              {deadline}
            </span>
          </div>
        </div>
      </div>

      {item.summary ? (
        <p className="mt-3 line-clamp-2 text-sm text-gray-700">
          {pickLocale(item.summary, item.summaryKo, item.summaryEn, locale)}
        </p>
      ) : null}
    </Link>
  );
}

function pickLocale(defaultValue, koValue, enValue, locale) {
  if (locale === "ko") return koValue || defaultValue || "";
  return enValue || defaultValue || "";
}

function formatDate(value, locale) {
  const d =
    typeof value === "string" || typeof value === "number"
      ? new Date(value)
      : value;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "-";

  try {
    return new Intl.DateTimeFormat(locale === "ko" ? "ko-KR" : "en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function Pagination({ page, totalPages, onChange, t }) {
  if (!totalPages || totalPages <= 1) return null;

  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className="flex items-center justify-center gap-2">
      <button
        className="rounded-xl border px-3 py-2 text-sm disabled:opacity-40"
        disabled={!canPrev}
        onClick={() => onChange(page - 1)}
      >
        {t("employment.pagination.prev")}
      </button>

      <div className="text-sm text-gray-600">
        {t("employment.result.page", { current: page + 1, total: totalPages })}
      </div>

      <button
        className="rounded-xl border px-3 py-2 text-sm disabled:opacity-40"
        disabled={!canNext}
        onClick={() => onChange(page + 1)}
      >
        {t("employment.pagination.next")}
      </button>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="grid gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="h-5 w-2/3 animate-pulse rounded bg-gray-100" />
          <div className="mt-2 h-4 w-1/2 animate-pulse rounded bg-gray-100" />
          <div className="mt-4 h-4 w-full animate-pulse rounded bg-gray-100" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, message, onReset, resetLabel }) {
  return (
    <div className="rounded-2xl border bg-white p-10 text-center shadow-sm">
      <div className="text-lg font-semibold">{title}</div>
      <p className="mt-2 text-sm text-gray-600">{message}</p>
      <div className="mt-6 flex justify-center">
        <button
          className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
          onClick={onReset}
        >
          {resetLabel}
        </button>
      </div>
    </div>
  );
}

function ErrorState({ title, message, onReset, resetLabel }) {
  return (
    <div className="rounded-2xl border bg-white p-10 text-center shadow-sm">
      <div className="text-lg font-semibold">{title}</div>
      <p className="mt-2 text-sm text-gray-600">{message}</p>
      <div className="mt-6 flex justify-center">
        <button
          className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
          onClick={onReset}
        >
          {resetLabel}
        </button>
      </div>
    </div>
  );
}
